import React from 'react';
import { useTranslation } from 'react-i18next';
import type { VariationChange } from '../utils/variationChanges';
import { TransactionKindPill } from './TransactionKindPill';

/** A transaction-list row with an ÄTA mixed in at its signing time, newest first. */
export type TransactionListRow<T> = T | { type: 'variation_signed'; id: string; change: VariationChange };

const rowTime = (row: { occurredAt?: string; createdAt?: string }) =>
  new Date(row.occurredAt || row.createdAt || 0).getTime();

/**
 * Mixes the signed ÄTAs into the transaction list (ClickUp 869fnvpvp), so the
 * change a card speaks of can be followed to the list: each ÄTA comes before
 * the first transaction that happened before it was signed.
 */
export function withVariationRows<T extends { occurredAt?: string; createdAt?: string }>(
  transactions: T[],
  changes: VariationChange[]
): Array<TransactionListRow<T>> {
  const rows: Array<TransactionListRow<T>> = [...transactions];
  for (const change of [...changes].sort((a, b) => a.signedAt.localeCompare(b.signedAt))) {
    const signedAt = new Date(change.signedAt).getTime();
    const index = rows.findIndex(row => (row as { type?: string }).type !== 'variation_signed' && rowTime(row as T) < signedAt);
    rows.splice(index === -1 ? rows.length : index, 0, { type: 'variation_signed', id: `variation:${change.contractId}`, change });
  }
  return rows;
}

/**
 * A signed ÄTA as a row: a minus post when it made the project cheaper. The
 * Beställare reads about themselves as "du", others about "beställaren".
 */
export function VariationTransactionItem({ change, isBuyer = false }: { change: VariationChange; isBuyer?: boolean }) {
  const { t } = useTranslation();
  const lower = change.deltaOre < 0;
  const amount = `${change.deltaOre === 0 ? '' : lower ? '−' : '+'}${new Intl.NumberFormat('sv-SE').format(Math.abs(change.deltaOre) / 100)} kr`;
  return (
    <div className="border rounded overflow-hidden" data-testid="variation-transaction" data-contract-id={change.contractId}>
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2 p-3 bg-light">
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <TransactionKindPill kind="variation" />
            <span className="fw-bold">
              {change.number != null
                ? t('projectView.transactions.variation.title', { number: change.number })
                : t('projectView.transactions.variation.titleNoNumber')}
            </span>
          </div>
          <small className="text-muted">
            {t(change.deltaOre === 0
              ? 'projectView.transactions.variation.unchanged'
              : `projectView.transactions.variation.${lower ? 'lower' : 'higher'}${isBuyer ? 'Buyer' : ''}`)}
          </small>
        </div>
        <div className="text-sm-end flex-shrink-0">
          <div className={`fs-5 fw-bold text-nowrap ${lower ? 'text-warning' : 'text-success'}`}>{amount}</div>
          <small className="text-muted text-nowrap">
            {new Date(change.signedAt).toLocaleString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </small>
        </div>
      </div>
    </div>
  );
}
